import React from 'react';
import { Card, CardContent } from './ui/card';
import { BookOpen, Heart, Users, Sparkles, Globe, GraduationCap } from 'lucide-react';

export const Mascot = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-goldenOlive/10 px-6 py-2 rounded-full mb-4">
            <span className="text-goldenOlive font-semibold">Our Identity</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">Doctrina Charter School Mascot: Quetzalcoatl</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            The feathered serpent deity from Mesoamerican civilization reflects both the <span className="font-semibold text-goldenOlive">cultural heritage</span> of our student population and our <span className="font-semibold text-tealAccent">educational mission</span>.
          </p>
        </div>

        {/* Hero Visual */}
        <div className="mb-16 relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img 
              src={process.env.REACT_APP_QUETZALCOATL_IMAGE_URL} 
              alt="Quetzalcoatl - The Feathered Serpent" 
              className="w-full h-full object-cover" style={{maxHeight: '400px'}}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navyBlue/80 via-navyBlue/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white text-center">
              <h3 className="text-5xl sm:text-6xl font-bold mb-4">Quetzalcoatl</h3>
              <p className="text-3xl italic mb-3">The Feathered Serpent</p>
              <p className="text-xl max-w-3xl mx-auto">
                God of Learning, Science, and Knowledge across Aztec, Mayan, and Toltec civilizations
              </p>
            </div>
          </div>
        </div>

        {/* Three Core Values */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card className="border-2 border-tealAccent/20 hover:border-tealAccent/40 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="bg-tealAccent/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="text-tealAccent" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-navyBlue mb-3">Knowledge & Learning</h3>
              <p className="text-gray-700 leading-relaxed">
                Quetzalcoatl was the god of learning, science, and knowledge, credited with bringing written language, mathematics, and the arts to humanity.
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-goldenOlive/20 hover:border-goldenOlive/40 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="bg-goldenOlive/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="text-goldenOlive" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-navyBlue mb-3">Transformation & Growth</h3>
              <p className="text-gray-700 leading-relaxed">
                The feathered serpent symbolizes unity of earth (serpent) and sky (feathers), representing transcendence and the educational journey from earthly concerns to higher knowledge.
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-sageGreen/20 hover:border-sageGreen/40 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="bg-sageGreen/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="text-sageGreen" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-navyBlue mb-3">Creation & Innovation</h3>
              <p className="text-gray-700 leading-relaxed">
                Associated with creativity, wind, and the morning star, Quetzalcoatl represents the innovative, forward-thinking approach we bring to middle school education.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Cultural Significance Section */}
        <div className="mb-16">
          <div className="bg-navyBlue rounded-2xl p-8 sm:p-12 text-white mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <Globe className="text-tealAccent" size={40} />
              <h3 className="text-3xl font-bold">Cultural Significance</h3>
            </div>
            <div className="space-y-4 text-lg text-white/90 leading-relaxed">
              <p>
                The Winchester area and broader Inland Empire region are home to a significant Latino population, many with ancestry tracing to Mexico and Central America where Aztec, Mayan, and other Indigenous civilizations flourished.
              </p>
              <p>
                <span className="font-semibold text-tealAccent">Quetzalcoatl represents one of the most important deities across multiple Mesoamerican cultures</span>, revered by the Aztecs, Toltecs, and Maya alike. This choice honors the Indigenous heritage that forms a foundational part of many of our students' cultural identities.
              </p>
            </div>
          </div>
        </div>

        {/* Educational Connection */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-navyBlue mb-8 text-center">Educational Connection</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-tealAccent/10 to-white rounded-2xl p-8 border-l-4 border-tealAccent">
              <div className="flex items-start space-x-4 mb-4">
                <Heart className="text-tealAccent flex-shrink-0 mt-1" size={28} />
                <div>
                  <h4 className="text-xl font-bold text-navyBlue mb-3">Cultural Affirmation & Belonging</h4>
                  <p className="text-gray-700 leading-relaxed">
                    By choosing Quetzalcoatl, we send a powerful message to our students that their cultural heritage is valued, honored, and integrated into their school identity. For many Latino students, particularly those of Mexican descent, seeing their ancestral culture reflected in their school mascot provides a <span className="font-semibold">sense of belonging and pride</span>.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-goldenOlive/10 to-white rounded-2xl p-8 border-l-4 border-goldenOlive">
              <div className="flex items-start space-x-4 mb-4">
                <BookOpen className="text-goldenOlive flex-shrink-0 mt-1" size={28} />
                <div>
                  <h4 className="text-xl font-bold text-navyBlue mb-3">Curriculum Integration</h4>
                  <p className="text-gray-700 leading-relaxed">
                    The mascot choice creates natural opportunities for <span className="font-semibold">culturally responsive curriculum integration</span>, allowing students to explore Mesoamerican mathematics, astronomy, architecture, and literature while meeting California state standards.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-sageGreen/10 rounded-2xl p-8 border-l-4 border-sageGreen">
            <p className="text-lg text-gray-700 leading-relaxed italic text-center">
              "This cultural affirmation supports <span className="font-semibold text-sageGreen">academic engagement</span> and helps students develop <span className="font-semibold text-sageGreen">positive cultural identity</span>—both linked to improved educational outcomes."
            </p>
          </div>
        </div>

        {/* Community Representation */}
        <div className="bg-gradient-to-br from-coralRed/10 via-goldenOlive/10 to-tealAccent/10 rounded-2xl p-8 sm:p-12 border-2 border-coralRed/20">
          <div className="flex items-center space-x-4 mb-6">
            <Users className="text-coralRed" size={40} />
            <h3 className="text-3xl font-bold text-navyBlue">Community Representation</h3>
          </div>
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>
              Our mascot selection demonstrates <span className="font-semibold text-coralRed">Doctrina's commitment to serving and reflecting the Winchester community</span>. Rather than adopting generic or culturally disconnected imagery, we've chosen a symbol that resonates with the lived experiences and heritage of the families we serve.
            </p>
            <p>
              This choice introduces <span className="font-semibold">all students</span> to the rich intellectual and cultural achievements of Mesoamerican civilizations, fostering cross-cultural understanding and appreciation.
            </p>
          </div>
        </div>

        {/* Closing Statement */}
        <div className="mt-16 text-center">
          <div className="bg-navyBlue rounded-2xl p-8 sm:p-12 text-white">
            <div className="text-6xl mb-6">🌟</div>
            <h3 className="text-3xl font-bold mb-6">A Legacy of Excellence</h3>
            <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
              Quetzalcoatl stands as a reminder that our students come from <span className="font-semibold text-tealAccent">lineages of scholars, scientists, mathematicians, and innovators</span>—a legacy that Doctrina Charter School is honored to continue.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Mascot;